import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Crown, TrendingUp, RefreshCw, Sparkles, User, ShoppingBag, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { mastershopHelpers, getCurrentYearMonth, formatMonthName } from '../../helpers/mastershopHelpers';
import { LeaderboardSeller } from '../../types/sellerIntegration';

interface OctoberLeaderboardProps {
  onBack?: () => void;
  currentUserId?: string;
  initialMonth?: string;
}

export const OctoberLeaderboard: React.FC<OctoberLeaderboardProps> = ({ onBack, currentUserId, initialMonth }) => {
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth || getCurrentYearMonth());
  const [sellers, setSellers] = useState<LeaderboardSeller[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadLeaderboard();
  }, [selectedMonth]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const { data, error } = await mastershopHelpers.getLeaderboard(selectedMonth);
      if (!error && data) {
        setSellers(data);
      }
    } catch (err) {
      console.error('Erro ao carregar ranking:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadLeaderboard();
    } finally {
      setRefreshing(false);
    }
  };

  const formatMoney = (val: number, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency === 'COP' ? 'COP' : 'BRL',
      maximumFractionDigits: 0,
    }).format(val);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const top3 = sellers.slice(0, 3);
  const restOfList = sellers.slice(3);

  return (
    <div className="bg-[#121316] min-h-screen text-white p-4 sm:p-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-800 pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-[#0FFF55]/20 border border-[#0FFF55]/40 rounded-xl">
                <Trophy className="w-6 h-6 text-[#0FFF55]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                Ranking de Vendas • {formatMonthName(selectedMonth)}
              </h1>
            </div>
            <p className="text-gray-400 text-sm">
              Competição oficial dos maiores vendedores do Master Club com integração Master Shop.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Seletor de Mês */}
            <div className="flex items-center gap-1.5 bg-[#1a1d24] p-1 rounded-xl border border-gray-800">
              <button
                onClick={() => setSelectedMonth('2026-09')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  selectedMonth === '2026-09'
                    ? 'bg-[#0FFF55] text-[#121316]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Setembro (Atual)
              </button>
              <button
                onClick={() => setSelectedMonth('2026-10')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  selectedMonth === '2026-10'
                    ? 'bg-[#0FFF55] text-[#121316]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Outubro (Competição)
              </button>
            </div>

            {onBack && (
              <Button
                onClick={onBack}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800 rounded-xl"
              >
                Voltar
              </Button>
            )}
            <Button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-bold rounded-xl flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center space-y-4">
            <RefreshCw className="w-10 h-10 text-[#0FFF55] animate-spin mx-auto" />
            <p className="text-gray-400">Carregando classificação de {formatMonthName(selectedMonth)}...</p>
          </div>
        ) : sellers.length === 0 ? (
          <div className="bg-[#1a1d24] border border-gray-800 rounded-3xl p-12 text-center space-y-4">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto" />
            <h3 className="text-xl font-bold text-white">Ainda não há vendas computadas em {formatMonthName(selectedMonth)}</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Conecte sua chave da Master Shop no seu Perfil para começar a pontuar e figurar entre os líderes!
            </p>
          </div>
        ) : (
          <>
            {/* Pódio TOP 3 */}
            {top3.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                {/* 2º Lugar */}
                {top3[1] && (
                  <div className="order-2 md:order-1 bg-gradient-to-b from-gray-800/60 to-gray-900/80 border border-slate-600/50 rounded-3xl p-6 text-center relative shadow-xl transform md:translate-y-4">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-slate-400 text-gray-950 font-black text-xs px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                      <Medal className="w-3.5 h-3.5" /> 2º LUGAR
                    </div>
                    <Avatar className="w-20 h-20 mx-auto ring-4 ring-slate-400/50 shadow-xl mb-3 mt-2">
                      <AvatarImage src={top3[1].avatar_url} />
                      <AvatarFallback className="bg-slate-700 text-white font-bold text-xl">
                        {getInitials(top3[1].full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-extrabold text-lg text-white truncate">{top3[1].full_name}</h3>
                    <p className="text-2xl font-black text-slate-300 mt-2">{formatMoney(top3[1].total_revenue, top3[1].currency)}</p>
                    <span className="inline-block text-xs font-semibold text-gray-400 mt-1">
                      {top3[1].total_orders} pedidos
                    </span>
                  </div>
                )}

                {/* 1º Lugar (Campeão) */}
                {top3[0] && (
                  <div className="order-1 md:order-2 bg-gradient-to-b from-yellow-500/20 via-[#1e222b] to-[#16181f] border-2 border-yellow-400/80 rounded-3xl p-8 text-center relative shadow-2xl shadow-yellow-500/10">
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-amber-300 text-gray-950 font-black text-sm px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Crown className="w-4 h-4 text-yellow-950" /> 1º CAMPEÃO
                    </div>
                    <Avatar className="w-24 h-24 mx-auto ring-4 ring-yellow-400 shadow-2xl mb-4 mt-2">
                      <AvatarImage src={top3[0].avatar_url} />
                      <AvatarFallback className="bg-yellow-400 text-gray-950 font-black text-2xl">
                        {getInitials(top3[0].full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-black text-xl text-white truncate">{top3[0].full_name}</h3>
                    <div className="inline-block bg-yellow-500/20 text-yellow-300 font-black text-xs px-3 py-0.5 rounded-full mt-1 border border-yellow-500/40">
                      Líder do Mês 👑
                    </div>
                    <p className="text-3xl sm:text-4xl font-black text-[#0FFF55] mt-3">
                      {formatMoney(top3[0].total_revenue, top3[0].currency)}
                    </p>
                    <span className="inline-block text-xs font-bold text-gray-300 mt-1">
                      {top3[0].total_orders} pedidos processados
                    </span>
                  </div>
                )}

                {/* 3º Lugar */}
                {top3[2] && (
                  <div className="order-3 bg-gradient-to-b from-amber-950/40 to-gray-900/80 border border-amber-700/50 rounded-3xl p-6 text-center relative shadow-xl transform md:translate-y-6">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-600 text-white font-black text-xs px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                      <Medal className="w-3.5 h-3.5" /> 3º LUGAR
                    </div>
                    <Avatar className="w-20 h-20 mx-auto ring-4 ring-amber-600/50 shadow-xl mb-3 mt-2">
                      <AvatarImage src={top3[2].avatar_url} />
                      <AvatarFallback className="bg-amber-800 text-white font-bold text-xl">
                        {getInitials(top3[2].full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-extrabold text-lg text-white truncate">{top3[2].full_name}</h3>
                    <p className="text-2xl font-black text-amber-300 mt-2">{formatMoney(top3[2].total_revenue, top3[2].currency)}</p>
                    <span className="inline-block text-xs font-semibold text-gray-400 mt-1">
                      {top3[2].total_orders} pedidos
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Tabela de Classificação Completa */}
            <div className="bg-[#1a1d24]/90 border border-gray-800 rounded-3xl overflow-hidden shadow-2xl">
              <div className="p-6 border-b border-gray-800 flex items-center justify-between">
                <h3 className="font-bold text-lg text-white">Classificação Geral • {formatMonthName(selectedMonth)}</h3>
                <span className="text-xs text-gray-400">Total de participantes: {sellers.length}</span>
              </div>

              <div className="divide-y divide-gray-800/60">
                {sellers.map((seller) => {
                  const isCurrent = currentUserId === seller.user_id;
                  return (
                    <div
                      key={seller.user_id}
                      className={`p-4 sm:p-5 flex items-center justify-between transition-colors ${
                        isCurrent ? 'bg-[#0FFF55]/10 border-l-4 border-[#0FFF55]' : 'hover:bg-gray-800/40'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-8 text-center font-black text-base sm:text-lg ${
                          seller.rank === 1 ? 'text-yellow-400' : seller.rank === 2 ? 'text-slate-300' : seller.rank === 3 ? 'text-amber-500' : 'text-gray-500'
                        }`}>
                          #{seller.rank}
                        </span>

                        <Avatar className="w-11 h-11 ring-2 ring-gray-700">
                          <AvatarImage src={seller.avatar_url} />
                          <AvatarFallback className="bg-gray-800 text-gray-200 font-bold">
                            {getInitials(seller.full_name)}
                          </AvatarFallback>
                        </Avatar>

                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-white text-sm sm:text-base">
                              {seller.full_name}
                            </h4>
                            {isCurrent && (
                              <span className="bg-[#0FFF55] text-[#121316] font-black text-[10px] px-2 py-0.5 rounded-full">
                                VOCÊ
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <ShoppingBag className="w-3 h-3 text-gray-500" />
                            {seller.total_orders} pedidos
                          </span>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-base sm:text-xl font-black text-[#0FFF55] block">
                          {formatMoney(seller.total_revenue, seller.currency)}
                        </span>
                        <span className="text-[11px] font-semibold text-gray-400">
                          Meta: {formatMoney(seller.current_milestone_target, seller.currency)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
